import type { LanguageCode } from "@/config/i18n";
import en from "@/locales/en";
import ja from "@/locales/ja";
import ko from "@/locales/ko";

export type Translations = typeof en;

/** Dotted paths to every string leaf, e.g. `"movies.title"`. */
type Leaves<T, Prefix extends string = ""> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : T[K] extends Record<string, unknown>
      ? Leaves<T[K], `${Prefix}${K}.`>
      : never;
}[keyof T & string];

export type TranslationKey = Leaves<Translations>;

export type TranslationParams = Record<string, string | number>;

export type Translator = (
  key: TranslationKey,
  params?: TranslationParams,
) => string;

const translations: Record<LanguageCode, unknown> = { en, ko, ja };

function lookup(source: unknown, key: string): string | undefined {
  let value: unknown = source;

  for (const part of key.split(".")) {
    if (value === null || typeof value !== "object") return undefined;
    value = (value as Record<string, unknown>)[part];
  }

  return typeof value === "string" ? value : undefined;
}

export function getTranslations(lang: LanguageCode): Translations {
  return (translations[lang] ?? translations.en) as Translations;
}

const pluralRules = new Map<LanguageCode, Intl.PluralRules>();

/** Whether `count` takes the plural form ("2 movies") in this language. */
function isPlural(lang: LanguageCode, count: number) {
  let rules = pluralRules.get(lang);

  if (!rules) {
    rules = new Intl.PluralRules(lang);
    pluralRules.set(lang, rules);
  }

  return rules.select(count) !== "one";
}

/**
 * Create a translation function for a language.
 *
 * Missing keys fall back to English, then to the key itself, so a missing
 * translation never crashes a page. `{placeholders}` are interpolated.
 *
 * Plurals: when `params.count` is a number that takes the plural form in this
 * language, the `<key>_plural` string is used if there is one. Languages
 * without plural forms (Korean, Japanese) give it the same text.
 *
 * @example
 * const t = createTranslator("en");
 * t("movies.minimumOf", { label: "IMDb" }); // "Minimum IMDb"
 * t("movies.resultCount", { count: 1 }); // "1 movie found"
 * t("movies.resultCount", { count: 2 }); // "2 movies found" (resultCount_plural)
 */
export function createTranslator(lang: LanguageCode): Translator {
  const source = translations[lang] ?? translations.en;

  return function translate(key, params) {
    const count = params?.count;
    const pluralKey =
      typeof count === "number" && isPlural(lang, count)
        ? `${key}_plural`
        : undefined;
    const value =
      (pluralKey && lookup(source, pluralKey)) ??
      lookup(source, key) ??
      (pluralKey && lookup(translations.en, pluralKey)) ??
      lookup(translations.en, key) ??
      key;

    if (!params) return value;

    return value.replace(/\{(\w+)\}/g, (match, paramKey: string) =>
      params[paramKey] !== undefined ? String(params[paramKey]) : match,
    );
  };
}
